import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../../services/api';

const FAMILY_COLORS = ['#556B2F', '#8B4513', '#2E4A62', '#6B8E23', '#7B1FA2', '#1565C0'];
const getFamilyColor = (name) =>
  FAMILY_COLORS[
    (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % FAMILY_COLORS.length
  ];

/**
 * Multi-family recipient selector for time capsules.
 * Groups members by family, supports search and family filtering.
 *
 * Props:
 *   families - array of { id, name, member_count } from FamilyContext
 *   onSelect - callback(member) when a recipient is selected
 *   selectedRecipient - currently selected member object (or null)
 *   currentPersonId - current user's person_id to exclude from list
 */
function RecipientSelector({ families, onSelect, selectedRecipient, currentPersonId }) {
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Fetch members from all families
  useEffect(() => {
    const fetchAllMembers = async () => {
      if (!families || families.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const results = await Promise.allSettled(
          families.map(f => axiosInstance.get(`/families/${f.id}/members/`))
        );

        const memberMap = new Map();

        results.forEach((result, idx) => {
          if (result.status !== 'fulfilled') return;
          const family = families[idx];
          const membersData = result.value.data.members || result.value.data.results || result.value.data || [];

          membersData.forEach(member => {
            if (member.id === currentPersonId) return;

            if (memberMap.has(member.id)) {
              // Member exists in multiple families - add this family
              const existing = memberMap.get(member.id);
              existing.families.push({ id: family.id, name: family.name });
            } else {
              memberMap.set(member.id, {
                ...member,
                first_name: member.first_name || 'Unknown',
                last_name: member.last_name || '',
                role: member.role || 'Family member',
                gender: member.gender,
                families: [{ id: family.id, name: family.name }],
              });
            }
          });
        });

        setAllMembers(Array.from(memberMap.values()));
      } catch (err) {
        console.error('Error fetching family members:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllMembers();
  }, [families, currentPersonId]);

  // Filter and group members
  const filteredMembers = useMemo(() => {
    let filtered = allMembers;

    // Filter by family
    if (activeFilter !== 'all') {
      filtered = filtered.filter(m => m.families.some(f => f.id === activeFilter));
    }

    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(m =>
        (m.first_name || '').toLowerCase().includes(q) ||
        (m.last_name || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [allMembers, activeFilter, search]);

  // Group members by their primary family
  const groupedMembers = useMemo(() => {
    const groups = {};

    filteredMembers.forEach(member => {
      const familyId = activeFilter !== 'all'
        ? activeFilter
        : member.families[0]?.id;
      const familyName = activeFilter !== 'all'
        ? families.find(f => f.id === activeFilter)?.name || 'Family'
        : member.families[0]?.name || 'Family';

      if (!groups[familyId]) {
        groups[familyId] = { name: familyName, members: [] };
      }
      groups[familyId].members.push(member);
    });

    return groups;
  }, [filteredMembers, activeFilter, families]);

  const getInitial = (member) => {
    return (member.first_name?.[0] || '?').toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Family filter tabs */}
      {families.length > 1 && (
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setActiveFilter('all')}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
              activeFilter === 'all'
                ? 'bg-[#556B2F] text-white'
                : 'bg-[#F0F4E8] text-[#556B2F] hover:bg-[#E0E8D0]'
            }`}
          >
            All Families
          </button>
          {families.map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
                activeFilter === f.id
                  ? 'bg-[#556B2F] text-white'
                  : 'bg-[#F0F4E8] text-[#556B2F] hover:bg-[#E0E8D0]'
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="relative mb-3">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#6B8E23]"
        />
      </div>

      {/* Grouped member list */}
      <div className="space-y-4 max-h-[360px] overflow-y-auto">
        {Object.entries(groupedMembers).map(([familyId, group]) => (
          <div key={familyId}>
            {/* Family section header */}
            <div className="flex items-center gap-2 mb-2 px-1">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: getFamilyColor(group.name) }}
              />
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                🏠 {group.name}
              </span>
            </div>

            {/* Member cards */}
            <div className="space-y-1">
              {group.members.map((member) => {
                const isSelected = selectedRecipient?.id === member.id;
                const isShared = member.families.length > 1;
                const color = getFamilyColor(member.families[0]?.name || '');

                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => onSelect(member)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                      isSelected
                        ? 'bg-[#F0F4E8] border-2 border-[#6B8E23]'
                        : 'bg-white border border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    {/* Avatar */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[14px] font-bold flex-shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      {getInitial(member)}
                    </div>

                    {/* Name + role */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-gray-900 truncate">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {member.role}
                      </div>
                    </div>

                    {/* Shared badge */}
                    {isShared && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#FFF3E0] text-[#E65100] flex-shrink-0">
                        Shared
                      </span>
                    )}

                    {/* Selection indicator */}
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${
                      isSelected
                        ? 'bg-[#6B8E23]'
                        : 'border-2 border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {filteredMembers.length === 0 && (
          <div className="text-center py-6 text-gray-400 text-[13px]">
            {search ? 'No members found matching your search' : 'No family members found'}
          </div>
        )}
      </div>

      {/* Selection summary */}
      {selectedRecipient && (
        <div className="mt-3 px-3 py-2 bg-[#F0F4E8] rounded-lg flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0"
            style={{ backgroundColor: getFamilyColor(selectedRecipient.families?.[0]?.name || '') }}
          >
            {getInitial(selectedRecipient)}
          </div>
          <span className="text-[13px] font-medium text-[#556B2F]">
            Writing to {selectedRecipient.first_name}
          </span>
        </div>
      )}
    </div>
  );
}

export default RecipientSelector;
